import React from "react";
import { useAnketaI18n } from "src/internationalization";

/**
 * Public, marketing-facing language switcher.
 *
 * The app-wide switcher lives inside the authenticated avatar dropdown, which a
 * first-time visitor never opens. Competitor security homepages (XM Cyber,
 * Arctic Wolf) surface the locale toggle prominently, so the marketing homepage
 * gets its own visible segmented control. It drives the same `setLanguage`
 * (persisted to localStorage by the i18next detector), so the choice carries
 * over into the rest of the app.
 *
 * Rendered on the navy hero by default (`variant="on-navy"`), so colors use the
 * `--ak-on-navy-*` tokens. The same control is reused inside the authenticated
 * avatar dropdown, whose panel is a light/dark surface rather than navy - pass
 * `variant="surface"` there so it stays legible against the panel background.
 */
export const HomepageLangSwitch: React.FC<{
  style?: React.CSSProperties;
  variant?: "on-navy" | "surface";
}> = ({ style, variant = "on-navy" }) => {
  const { languages, currentLang, setLanguage, translate } = useAnketaI18n();

  const onNavy = variant === "on-navy";

  return (
    <div
      role="group"
      aria-label={translate("anketa.language", { defaultMessage: "Language" })}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        padding: 3,
        borderRadius: 999,
        background: onNavy ? "var(--ak-on-navy-surface-2)" : "var(--ak-surface-2)",
        border: `1px solid ${onNavy ? "var(--ak-on-navy-border)" : "var(--ak-border)"}`,
        backdropFilter: onNavy ? "blur(8px)" : undefined,
        WebkitBackdropFilter: onNavy ? "blur(8px)" : undefined,
        ...style,
      }}
    >
      {languages.map((lang) => {
        const active = currentLang === lang.code;
        return (
          <button
            key={lang.code}
            type="button"
            onClick={() => setLanguage(lang.code)}
            aria-pressed={active}
            title={lang.label}
            style={{
              cursor: active ? "default" : "pointer",
              border: "none",
              outline: "none",
              borderRadius: 999,
              // ≥36px tall hit area for touch (UI/UX touch-target guideline)
              padding: "9px 16px",
              minHeight: 36,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              transition: "background 0.2s ease, color 0.2s ease",
              background: active
                ? "linear-gradient(135deg, var(--ak-accent) 0%, var(--ak-accent-strong) 100%)"
                : "transparent",
              color: active
                ? "var(--ak-text-on-accent)"
                : onNavy
                  ? "var(--ak-on-navy-secondary)"
                  : "var(--ak-text-secondary)",
              boxShadow: active ? "0 2px 8px var(--ak-accent-glow)" : "none",
            }}
          >
            {lang.code}
          </button>
        );
      })}
    </div>
  );
};

export default HomepageLangSwitch;
